import {showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {useRef, useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {
  useCancelWhatsAppSignupMutation,
  useCompleteWhatsAppSignupMutation,
  useStartWhatsAppSignupMutation,
  WhatsAppSignupFailureReason,
  WhatsAppSignupFailureReasonSchema,
  WhatsAppSignupResponseBody,
  WhatsAppSignupScenario,
} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {ApiError, noop, useOnMount} from 'shared/web';

import {FacebookLoginOptions, FacebookLoginResponse, FacebookSdk} from '../types/FacebookSdk';
import {loadFacebookSdk} from '../utils/loadFacebookSdk';
import {
  EmbeddedSignupMessage,
  parseEmbeddedSignupMessage,
} from '../utils/parseEmbeddedSignupMessage';

const GRAPH_API_VERSION = 'v25.0';
const SESSION_INFO_VERSION = '3';
const COEXISTENCE_FEATURE_TYPE = 'whatsapp_business_app_onboarding';
// Meta usually posts the finish message before the login callback. When only the code arrives,
// wait this long for the account ids before giving up; the code itself expires after 30 seconds.
const FINISH_MESSAGE_TIMEOUT_MS = 10_000;
// Recorded as the cancel step, so a missing finish message is told apart from a user cancel.
const FINISH_MESSAGE_TIMEOUT_STEP = 'finish_message_timeout';

type FinishedMessage = Extract<EmbeddedSignupMessage, {type: 'finished'}>;
type CancelDetails = {currentStep: string | null; errorCode: string | null};

export type WhatsAppSignupOutcome =
  | {state: 'cancelled'}
  | {state: 'expired'}
  | {state: 'failed'; failureReason: WhatsAppSignupFailureReason | null};

interface SignupAttempt {
  signupId: string;
  code: string | null;
  finish: FinishedMessage | null;
  dispose: () => void;
}

const NO_CANCEL_DETAILS: CancelDetails = {currentStep: null, errorCode: null};
const CANCELLED: WhatsAppSignupOutcome = {state: 'cancelled'};
const FAILED_WITHOUT_REASON: WhatsAppSignupOutcome = {state: 'failed', failureReason: null};

const showMetaUnavailable = () =>
  showNotification.error(i18n.t('page.settings.whatsApp.connection.metaUnavailable'));

// Responses are schema-checked only in development, so a reason added by a newer backend
// falls back to the generic failure instead of reaching the exhaustive message match.
const getKnownFailureReason = (failureReason: string | null) => {
  const parsed = WhatsAppSignupFailureReasonSchema.safeParse(failureReason);
  return parsed.success ? parsed.data : null;
};

const getLoginOptions = (signup: WhatsAppSignupResponseBody): FacebookLoginOptions => ({
  config_id: signup.configurationId,
  response_type: 'code',
  override_default_response_type: true,
  extras: {
    setup: {},
    sessionInfoVersion: SESSION_INFO_VERSION,
    ...(signup.scenario === 'coexistence' ? {featureType: COEXISTENCE_FEATURE_TYPE} : {}),
  },
});

const getOutcome = (signup: WhatsAppSignupResponseBody) =>
  match(signup.state)
    .returnType<WhatsAppSignupOutcome | null>()
    .with('completed', 'started', () => null)
    .with('cancelled', () => ({state: 'cancelled'}))
    .with('expired', () => ({state: 'expired'}))
    .with('failed', () => ({
      state: 'failed',
      failureReason: getKnownFailureReason(signup.failureReason),
    }))
    .otherwise(() => FAILED_WITHOUT_REASON);

/**
 * Runs one Meta Embedded Signup attempt: starts it on the backend, opens Meta's dialog and
 * hands the dialog's result back to the backend. Only the attempt started last is ever
 * completed or cancelled; results arriving for an older attempt are ignored.
 */
export function useWhatsAppEmbeddedSignup(onConnected?: () => void) {
  const [startSignup] = useStartWhatsAppSignupMutation();
  const [completeSignup, {isLoading: isCompleting}] = useCompleteWhatsAppSignupMutation();
  const [cancelSignup] = useCancelWhatsAppSignupMutation();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [outcome, setOutcome] = useState<WhatsAppSignupOutcome | null>(null);
  const activeAttemptRef = useRef<SignupAttempt | null>(null);
  const isUnmountedRef = useRef(false);

  // Best effort: the backend expires an unfinished signup on its own, and an error toast would
  // cover the outcome the user already sees.
  const cancelQuietly = (signupId: string, details: CancelDetails) =>
    cancelSignup({signupId, ...details})
      .unwrap()
      .catch(noop);

  // Preloading keeps the time between the click and Meta's popup short enough for popup blockers.
  useOnMount(
    () => {
      isUnmountedRef.current = false;
      loadFacebookSdk().catch(noop);
    },
    () => {
      isUnmountedRef.current = true;
      const attempt = activeAttemptRef.current;
      if (isNotNil(attempt)) {
        attempt.dispose();
        cancelQuietly(attempt.signupId, NO_CANCEL_DETAILS);
      }
    }
  );

  const launchDialog = (sdk: FacebookSdk, signup: WhatsAppSignupResponseBody) => {
    let finishTimeout: ReturnType<typeof setTimeout> | undefined;

    const attempt: SignupAttempt = {
      signupId: signup.id,
      code: null,
      finish: null,
      dispose: () => {
        window.removeEventListener('message', handleMessage);
        clearTimeout(finishTimeout);
        if (activeAttemptRef.current === attempt) {
          activeAttemptRef.current = null;
        }
      },
    };

    const isActive = () => activeAttemptRef.current === attempt;

    const complete = (code: string, finish: FinishedMessage) => {
      attempt.dispose();
      setIsDialogOpen(false);
      completeSignup({
        signupId: attempt.signupId,
        code,
        wabaId: finish.wabaId,
        phoneNumberId: finish.phoneNumberId,
      })
        .unwrap()
        .then((result) => {
          setOutcome(getOutcome(result));
          if (result.state === 'completed') {
            showNotification.success(i18n.t('page.settings.whatsApp.connection.connected'));
            onConnected?.();
          }
        })
        .catch((error: ApiError) => {
          // For example an expired signup; offer a retry next to the error.
          setOutcome(FAILED_WITHOUT_REASON);
          handleApiError(error);
        });
    };

    const abandon = (nextOutcome: WhatsAppSignupOutcome, details: CancelDetails) => {
      attempt.dispose();
      setIsDialogOpen(false);
      setOutcome(nextOutcome);
      cancelQuietly(attempt.signupId, details);
    };

    function handleMessage(event: MessageEvent) {
      const message = parseEmbeddedSignupMessage(event);
      if (isNil(message) || !isActive()) {
        return;
      }

      if (message.type === 'cancelled') {
        abandon(CANCELLED, {currentStep: message.currentStep, errorCode: message.errorCode});
        return;
      }

      attempt.finish = message;
      if (isNotNil(attempt.code)) {
        complete(attempt.code, message);
      }
    }

    const handleLoginResponse = (response: FacebookLoginResponse) => {
      if (!isActive()) {
        return;
      }

      const code = response.authResponse?.code;
      if (isNil(code)) {
        abandon(CANCELLED, NO_CANCEL_DETAILS);
        return;
      }

      if (isNotNil(attempt.finish)) {
        complete(code, attempt.finish);
        return;
      }

      attempt.code = code;
      finishTimeout = setTimeout(() => {
        if (isActive()) {
          // The user finished Meta's dialog, so this is a failure, not a cancel.
          abandon(FAILED_WITHOUT_REASON, {
            currentStep: FINISH_MESSAGE_TIMEOUT_STEP,
            errorCode: null,
          });
        }
      }, FINISH_MESSAGE_TIMEOUT_MS);
    };

    activeAttemptRef.current?.dispose();
    activeAttemptRef.current = attempt;
    window.addEventListener('message', handleMessage);

    // Meta's SDK is third-party code; keep its errors out of the API error handling below.
    try {
      sdk.init({
        appId: signup.appId,
        autoLogAppEvents: false,
        xfbml: false,
        version: GRAPH_API_VERSION,
      });
      sdk.login(handleLoginResponse, getLoginOptions(signup));
    } catch {
      attempt.dispose();
      setIsDialogOpen(false);
      cancelQuietly(attempt.signupId, NO_CANCEL_DETAILS);
      showMetaUnavailable();
    }
  };

  const connect = (scenario: WhatsAppSignupScenario) => {
    setOutcome(null);
    setIsDialogOpen(true);

    loadFacebookSdk()
      .then((sdk) =>
        startSignup({scenario})
          .unwrap()
          .then((signup) => {
            // The user left the page before the signup started; don't open Meta's dialog.
            if (isUnmountedRef.current) {
              cancelQuietly(signup.id, NO_CANCEL_DETAILS);
              return;
            }
            launchDialog(sdk, signup);
          })
          .catch((error: ApiError) => {
            setIsDialogOpen(false);
            handleApiError(error);
          })
      )
      .catch(() => {
        setIsDialogOpen(false);
        showMetaUnavailable();
      });
  };

  return {connect, outcome, isConnecting: isDialogOpen || isCompleting};
}
